import React, { useContext } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import NextLink from "next/link";
import { useAuth } from "@/providers/AuthenticationProvider";
import { AppContext } from "@/providers/AppContext";
import { useFeatureFlag } from "@/Hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/constants/featureFlags";
import {
  HEADER_MENU_CONFIG,
  HeaderMenuAction,
  HeaderMenuDropdownConfig,
  HeaderMenuEntryConfig,
  HeaderMenuItemConfig,
  HeaderMenuLinkConfig
} from "./headerMenuConfig";
import { HeaderMenuAccessContext, isHeaderMenuEntryVisible } from "./headerMenuAccess";

/**
 * Menu entries render as real links (via next/link) so they support middle/ctrl/cmd-click and the
 * browser's "Open in new tab" — the new tab borrows the session from an open tab (crossTabAuth).
 * A plain left click is still a client-side route change, so the same-tab experience doesn't reload.
 */
const getActionLinkProps = (action: HeaderMenuAction) =>
  action.type === "navigate" ? { href: action.path } : { href: action.url, target: "_blank", rel: "noopener noreferrer" };

const isMenuItemActive =(item: HeaderMenuItemConfig, currentPath: string): boolean => {
  if (!item.activePath) {
    return false;
  }

  if (item.activePathMatch === "startsWith") {
    return currentPath.startsWith(item.activePath);
  }

  return currentPath === item.activePath;
};

export default function HeaderMenu() {
  const { apiEnv } = useAuth();
  const { admin, isBtdUser } = useContext(AppContext);
  const isPlcWorkspaceEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_PLC_WORKSPACE);
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const currentPath = window.location.pathname;

  const accessContext: HeaderMenuAccessContext = {
    apiEnv,
    auth: {
      admin,
      isBtdUser
    },
    featureFlags: {
      ENABLE_PLC_WORKSPACE: isPlcWorkspaceEnabled
    }
  };

  const handleMenuOpen = (menuId: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenMenuId(menuId);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setOpenMenuId(null);
    setAnchorEl(null);
  };

  const renderLink = (entry: HeaderMenuLinkConfig) => (
    <Button
      key={entry.id}
      component={NextLink}
      prefetch={false}
      {...getActionLinkProps(entry.action)}
      className={entry.buttonClassName ?? "no-shadow"}
    >
      {entry.label}
    </Button>
  );

  const renderDropdown = (entry: HeaderMenuDropdownConfig) => {
    const visibleItems = entry.items.filter((item) => isHeaderMenuEntryVisible(item.access, accessContext));

    if (visibleItems.length === 0) {
      return null;
    }

    return (
      <React.Fragment key={entry.id}>
        <Button
          id={`${entry.id}-button`}
          onClick={handleMenuOpen(entry.id)}
          className="no-shadow"
          endIcon={<ExpandMoreIcon />}
        >
          {entry.label}
        </Button>
        <Menu
          id={`${entry.id}-menu`}
          anchorEl={anchorEl}
          open={openMenuId === entry.id}
          onClose={handleMenuClose}
          MenuListProps={{
            "aria-labelledby": `${entry.id}-button`,
            variant: "selectedMenu"
          }}
        >
          {visibleItems.map((item) => (
            <MenuItem
              key={item.id}
              component={NextLink}
              prefetch={false}
              {...getActionLinkProps(item.action)}
              className="no-shadow menu-item"
              onClick={handleMenuClose}
            >
              <div>{item.label}</div>
              {isMenuItemActive(item, currentPath) && <CheckIcon fontSize="large" />}
            </MenuItem>
          ))}
        </Menu>
      </React.Fragment>
    );
  };

  const renderEntry = (entry: HeaderMenuEntryConfig) => {
    if (!isHeaderMenuEntryVisible(entry.access, accessContext)) {
      return null;
    }

    if (entry.type === "link") {
      return renderLink(entry);
    }

    return renderDropdown(entry);
  };

  return <div className="app-menu">{HEADER_MENU_CONFIG.map(renderEntry)}</div>;
}
